import type {
  AddCartItemsResult,
  CartItemQuantity,
  CartStockRemoval,
} from '@/features/Cart/types/models';
import type {
  AddToCartItem,
  ShopifyCart,
  ShopifyCartItem,
} from '@/features/Cart/types/servicesShopifyCart';
import { ShopifyCartRequestError } from '@/features/Cart/services/shopifyCart';
import { getLineIdentity, getLineQuantities } from '@/features/Cart/services/lineItemProperties';

const STOCK_REJECTION_STATUS = 422;

export type CartMutationResult = {
  error: unknown;
  cartBefore: ShopifyCart | null;
  fetchedCart: ShopifyCart | null;
};

export type CartMutation = (
  mutate: () => Promise<ShopifyCart | void>,
  isTargeted: (item: ShopifyCartItem) => boolean,
) => Promise<CartMutationResult>;

export const isStockRejectionError = (error: unknown): boolean =>
  error instanceof ShopifyCartRequestError && error.status === STOCK_REJECTION_STATUS;

export const getUntargetedRemovals = (
  cartBefore: ShopifyCart,
  cartAfter: ShopifyCart,
  isTargeted: (item: ShopifyCartItem) => boolean,
): CartStockRemoval[] =>
  cartBefore.items.flatMap((item) => {
    if (isTargeted(item)) return [];
    const quantityAfter = cartAfter.items.find(({ key }) => key === item.key)?.quantity ?? 0;
    const removedQuantity = item.quantity - quantityAfter;
    return removedQuantity > 0 ? [{ title: item.title, quantity: removedQuantity }] : [];
  });

export const getLineTitles = (items: ShopifyCartItem[]): Map<string, string> =>
  new Map(items.map((item) => [getLineIdentity(item.variant_id, item.properties), item.title]));

export const getLinesByIdentity = (items: ShopifyCartItem[]): Map<string, ShopifyCartItem> =>
  new Map(items.map((item) => [getLineIdentity(item.variant_id, item.properties), item]));

export const getAddedQuantities = (
  items: AddToCartItem[],
  cartBefore: ShopifyCart,
  cartAfter: ShopifyCart,
): number[] => {
  const quantitiesBefore = getLineQuantities(cartBefore.items);
  const unclaimedQuantities = new Map(
    [...getLineQuantities(cartAfter.items)].map(([lineIdentity, quantity]) => [
      lineIdentity,
      Math.max(0, quantity - (quantitiesBefore.get(lineIdentity) ?? 0)),
    ]),
  );
  return items.map(({ id, properties, quantity }) => {
    const lineIdentity = getLineIdentity(id, properties);
    const unclaimedQuantity = unclaimedQuantities.get(lineIdentity) ?? 0;
    const addedQuantity = Math.min(unclaimedQuantity, quantity);
    unclaimedQuantities.set(lineIdentity, unclaimedQuantity - addedQuantity);
    return addedQuantity;
  });
};

export const getRejectedAddResult = (
  items: AddToCartItem[],
  isStockRejection: boolean,
): AddCartItemsResult => ({
  outcome: 'rejected',
  addedQuantities: items.map(() => 0),
  isStockRejection,
});

export const getFailedAddResult = (
  items: AddToCartItem[],
  error: unknown,
  cartBefore: ShopifyCart | null,
  cartAfter: ShopifyCart | null,
): AddCartItemsResult => {
  const isStockRejection = isStockRejectionError(error);
  if (!cartBefore || !cartAfter) {
    return { outcome: 'unconfirmed', addedQuantities: items.map(() => 0), isStockRejection };
  }
  const addedQuantities = getAddedQuantities(items, cartBefore, cartAfter);
  if (addedQuantities.every((quantity) => quantity === 0)) {
    return { outcome: 'rejected', addedQuantities, isStockRejection };
  }
  if (addedQuantities.every((quantity, index) => quantity >= items[index].quantity)) {
    return { outcome: 'complete', addedQuantities, isStockRejection: false };
  }
  return { outcome: 'partial', addedQuantities, isStockRejection };
};

export const getReducedLineQuantities = (
  cart: ShopifyCart,
  updates: CartItemQuantity[],
): Map<string, number> =>
  updates.reduce((quantities, { key, quantity }) => {
    const line = cart.items.find((item) => item.key === key);
    if (!line || quantity >= line.quantity) return quantities;
    return quantities.set(getLineIdentity(line.variant_id, line.properties), line.quantity);
  }, new Map<string, number>());
